import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Cropper from "react-easy-crop";

/* ── House-style tokens (matches admin modal styling, see Milling/Slots.jsx) ── */
const MODAL_LABEL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-muted)", marginBottom: 8, textTransform: "uppercase",
};

const MAX_OUTPUT_WIDTH = 1600;

/* Load an object URL into an <img> so we can draw it to a canvas */
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });

/**
 * Fixed-ratio crop step for banner images.
 * Props:
 *  - file      : File     — the image the admin picked
 *  - aspect    : number   — fixed crop ratio (default 8/3, mobile banner carousel)
 *  - onCancel  : () => void
 *  - onCropped : (croppedFile: File) => void — receives a JPEG File of the cropped area
 */
const BannerCropModal = ({ file, aspect = 8 / 3, onCancel, onCropped }) => {

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [cropError, setCropError] = useState("");

  const imageUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file]
  );

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  /* Esc closes without cropping */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {

    if (!croppedAreaPixels || processing) return;

    setProcessing(true);
    setCropError("");

    try {

      const img = await loadImage(imageUrl);

      const { x, y, width, height } = croppedAreaPixels;

      const outWidth = Math.max(
        1,
        Math.min(MAX_OUTPUT_WIDTH, Math.round(width))
      );

      const outHeight = Math.max(
        1,
        Math.round(outWidth * (height / width))
      );

      const canvas = document.createElement("canvas");
      canvas.width = outWidth;
      canvas.height = outHeight;

      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, x, y, width, height, 0, 0, outWidth, outHeight);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.85)
      );

      if (!blob) throw new Error("Could not create the cropped image");

      const croppedFile = new File(
        [blob],
        file.name.replace(/\.[^.]+$/, ".jpg"),
        { type: "image/jpeg" }
      );

      onCropped(croppedFile);

    } catch (err) {

      setCropError(err.message || "Cropping failed. Please try again.");
      setProcessing(false);

    }

  };

  if (!file) return null;

  return (
    <>
      {/* OVERLAY — sits above the banner form modal (zIndex 1040/1050) */}
      <div
        onClick={onCancel}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(14, 58, 44, 0.55)",
          backdropFilter: "blur(3px)",
          zIndex: 1060,
        }}
      />

      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1070,
          width: "calc(100% - 32px)",
          maxWidth: 720,
          background: "var(--mly-surface)",
          borderRadius: 20,
          boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
          padding: "28px 28px 24px",
          boxSizing: "border-box",
        }}
      >

        {/* HEADER */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 18,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: "var(--mly-text)",
            }}
          >
            Crop Banner Image
          </h2>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>

            <span
              style={{
                background: "var(--mly-accent-soft)",
                color: "var(--mly-accent)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1,
                padding: "4px 12px",
                borderRadius: 20,
                border: "1px solid var(--mly-accent)",
              }}
            >
              8 : 3 BANNER
            </span>

            <button
              onClick={onCancel}
              title="Close"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid var(--mly-border)",
                background: "var(--mly-surface-2)",
                cursor: "pointer",
                fontSize: 16,
                color: "var(--mly-text-soft)",
              }}
            >
              ×
            </button>

          </div>
        </div>

        <hr style={{ borderColor: "var(--mly-border-soft)", marginBottom: 18 }} />

        {/* ERROR */}
        {cropError && (
          <div
            style={{
              background: "#fef2f2",
              color: "#dc2626",
              padding: "10px 14px",
              borderRadius: 10,
              marginBottom: 14,
              fontSize: 13,
            }}
          >
            {cropError}
          </div>
        )}

        {/* CROP AREA */}
        <div
          style={{
            position: "relative",
            width: "100%",
            height: 280,
            borderRadius: 12,
            overflow: "hidden",
            background: "var(--milliyo-forest, #0E3A2C)",
            border: "1.5px solid var(--mly-border)",
          }}
        >
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={true}
          />
        </div>

        <div
          style={{
            fontSize: 11,
            color: "var(--mly-text-muted)",
            marginTop: 8,
            marginBottom: 16,
          }}
        >
          Drag to reposition. The banner is always cropped at the 8:3
          carousel ratio.
        </div>

        {/* ZOOM */}
        <div style={{ marginBottom: 22 }}>

          <label style={MODAL_LABEL}>Zoom</label>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>1×</span>

            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              style={{
                flex: 1,
                accentColor: "var(--milliyo-gold, #C9952A)",
                cursor: "pointer",
              }}
            />

            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>3×</span>

            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--mly-heading)",
                minWidth: 44,
                textAlign: "right",
              }}
            >
              {zoom.toFixed(2)}×
            </span>
          </div>

        </div>

        {/* BUTTONS */}
        <div style={{ display: "flex", gap: 12 }}>

          <button
            onClick={onCancel}
            disabled={processing}
            style={{
              padding: "11px 24px",
              borderRadius: 10,
              border: "1.5px solid var(--mly-border)",
              background: "var(--mly-surface-2)",
              color: "var(--mly-text-soft)",
              fontWeight: 600,
              fontSize: 14,
              cursor: processing ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            disabled={processing || !croppedAreaPixels}
            style={{
              flex: 1,
              padding: "11px 24px",
              borderRadius: 10,
              border: "none",
              background: processing
                ? "#e0c78a"
                : "var(--milliyo-gold-btn, linear-gradient(135deg, #C9952A, #b07f1d))",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: 0.3,
              cursor:
                processing || !croppedAreaPixels
                  ? "not-allowed"
                  : "pointer",
              boxShadow: processing
                ? "none"
                : "0 6px 16px rgba(201, 149, 42, 0.35)",
            }}
          >
            {processing ? "Cropping..." : "Use this crop"}
          </button>

        </div>

      </div>
    </>
  );
};

export default BannerCropModal;
